import { useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  hideRightPanel,
  selectRightPanelVisibility,
  showRightPanel,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import CampaignLogs from '../../components/CampaignLogs'
import RightPanel from '../../components/layout/RightPanel'
import {
  getContentHasVariants,
  getIsOptimized,
  getIsPersonalized,
} from '../../helpers'
import { useContent, useSelectedElement } from '../../hooks'
import { useVariantInsights } from '../context/VariantInsightsContext'

import CollapsedIcons from './CollapsedIcons'
import ExpandedIcons from './ExpandedIcons'
import ExperimentSettings from './experimentSettings'
import InsightsVariantManager from './InsightsVariantManager'

export type ActivePanel =
  | 'variantInspector'
  | 'advancedSettings'
  | 'insights'
  | 'campaignLogs'
export type HandleActivePanelChange = (panel: ActivePanel) => void

const RightSection = () => {
  const dispatch = useAppDispatch()
  const [activePanel, setActivePanel] = useState<ActivePanel | undefined>(
    undefined
  )

  const { content } = useContent()
  const { data: selectedElement } = useSelectedElement()
  const rightPanelVisibility = useAppSelector(selectRightPanelVisibility)
  const { state, clearVariantState } = useVariantInsights()

  const isOptimized = getIsOptimized(selectedElement)
  const isPersonalized = getIsPersonalized(selectedElement)
  const hasVariants = getContentHasVariants(content)

  const hasLoadingVariants = useMemo(() => {
    if (!content || !selectedElement) {
      return false
    }

    const wsMessages = content.ws_messages || []
    if (wsMessages.length === 0) {
      return false
    }

    return wsMessages.some((message) => {
      const topic = message.wsTopic
      // check if any of the topics indicate loading variants
      return (
        topic?.includes('/refreshVariant') ||
        topic?.includes('/moreLikeThis') ||
        topic?.includes('/tweakVariant') ||
        topic?.includes('/rejectVariant')
      )
    })
  }, [content, selectedElement])

  const handleActivePanelChange: HandleActivePanelChange = (
    panel: ActivePanel
  ) => {
    if (panel === activePanel) {
      setActivePanel(undefined)
      dispatch(hideRightPanel())
    } else {
      setActivePanel(panel)
      if (activePanel === 'variantInspector') {
        clearVariantState()
      }
      if (rightPanelVisibility !== 'expanded') {
        dispatch(showRightPanel())
      }
    }
  }

  useEffect(() => {
    if (state.isVariantSelected) {
      setActivePanel('variantInspector')
    } else if (!state.isVariantSelected && activePanel === 'variantInspector') {
      setActivePanel('insights')
    }
  }, [activePanel, state.isVariantSelected])

  const selectedElementId = selectedElement?.element_id

  // Depends on the element id, not the element object: the object gets a new
  // identity on every content cache write (e.g. selecting a variant), which
  // collapsed the panel right after a row click expanded it (visible flash).
  useEffect(() => {
    const shouldHidePanelDefault = !hasVariants
    const shouldHideRightPanel = shouldHidePanelDefault && !isPersonalized
    setActivePanel(undefined)

    if (!selectedElementId || shouldHideRightPanel) {
      dispatch(hideRightPanel({ visibility: 'none' }))
    } else {
      dispatch(hideRightPanel({ visibility: 'collapsed' }))
    }
  }, [dispatch, selectedElementId, isOptimized, hasVariants, isPersonalized])

  return (
    <RightPanel
      collapsedIcons={
        <CollapsedIcons
          handleActivePanelChange={handleActivePanelChange}
          hasLoadingVariants={hasLoadingVariants}
        />
      }
      onOutsideClick={() => {
        setActivePanel(undefined)
        dispatch(hideRightPanel())
      }}
    >
      {
        {
          variantInspector: (
            <InsightsVariantManager
              activePanel={activePanel}
              isOptimized={isOptimized}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          advancedSettings: (
            <ExperimentSettings
              activePanel={activePanel}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          insights: (
            <InsightsVariantManager
              activePanel={activePanel}
              isOptimized={isOptimized}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          campaignLogs: (
            <CampaignLogs
              expandedIcons={
                <ExpandedIcons
                  activePanel={activePanel}
                  handleActivePanelChange={handleActivePanelChange}
                  hasLoadingVariants={hasLoadingVariants}
                />
              }
            />
          ),
        }[activePanel ?? '']
      }
    </RightPanel>
  )
}

export default RightSection
